/**
 * Templates — the canonical-object bundle surface (Phase 2 lands the authoring).
 *
 * This Phase-0/1 stub establishes the "apply to many -> review the diff ->
 * confirm" gesture using the shared DiffView with a sample resolve diff, so the
 * one-review-gesture ritual is demonstrable before the catalog backend exists.
 * The Apply control is RoleGate-wrapped (org_admin) — the review + confirm is a
 * mutating action.
 */
import { useState } from "react";
import { DiffView } from "@/components/data/DiffView";
import { RoleGate } from "@/components/auth/RoleGate";
import { Button } from "@/components/ui/Button";
import { PageHeader, EmptyState } from "@/components/ui/Page";
import type { ConfigDiff } from "@/types/domain";

// A sample of the ONE canonical diff shape (what resolve()/AI/audit all emit).
const SAMPLE_DIFF: ConfigDiff = {
  summary: "3 changed · 1 added · 0 removed",
  blast_radius: "Applies to 2 sites · 5 devices affected",
  changes: [
    { op: "change", path: "ssids[0].ssid", before: "Acme-Corp", after: "Acme-Secure" },
    { op: "change", path: "ssids[0].security", before: "wpa2", after: "wpa3" },
    { op: "change", path: "ssids[0].passphrase", before: "••••", after: "••••", secret: true },
    { op: "add", path: "networks[1].vlan_id", after: 100 },
  ],
};

export default function Templates() {
  const [review, setReview] = useState(false);

  return (
    <div>
      <PageHeader
        title="Templates"
        subtitle="Reusable object bundles — authoring lands in Phase 2"
        actions={
          <RoleGate min="org_admin" mode="disable" reason="Customer admin required to apply a template.">
            <Button variant="primary" onClick={() => setReview(true)}>
              Preview apply
            </Button>
          </RoleGate>
        }
      />

      {review ? (
        <div className="max-w-2xl">
          <p className="mb-3 text-sm text-ink-muted">
            The identical review gesture used by AI proposals and config pushes:
          </p>
          <DiffView
            diff={SAMPLE_DIFF}
            confirmLabel="Apply to 5 devices"
            onDiscard={() => setReview(false)}
            onConfirm={() => setReview(false) /* Phase 2: resolve+push+audit */}
          />
        </div>
      ) : (
        <EmptyState
          title="No templates yet"
          hint="Catalog objects (SSID, Network, RADIUS, ACL…) and Templates arrive in Phase 2. Use “Preview apply” to see the shared review gesture."
        />
      )}
    </div>
  );
}
